import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import {
  FORWARD_ALLOWED_TENORS,
  getAuthenticatedWebSocketUrl,
  setSessionVariable,
} from "../Utils/CoTraderUtils";
import CoTraderForwardChart from "../Components/CoTraderForwardChartTileComponent";
import { CoHedgerHeaderHeader } from "../Components/CoHedger/CoHedgerHeader";
import CoHedgerPopOverMenu from "../Components/CoHedgerPopOverMenu";
import CoHedgerSummaryTileComponent from "../Components/CoHedgerSummaryTileComponent";
import CoHedgerLandingPage from "../Components/CoHedger/CoHedgerLandingPage";
const CurrencyList: any = {
  EURUSD: "EURUSD=X",
  USDJPY: "JPY=X",
  GBPUSD: "GBPUSD=X",
  USDCHF: "CHF=X",
  USDCAD: "CAD=X",
  AUDUSD: "AUDUSD=X",
  NZDUSD: "NZDUSD=X",
  USDNOK: "NOK=X",
  USDSEK: "SEK=X",
  USDUSD: "USD=X",
  USDINR: "USDINR=X",
};
interface Context {
  id?: {
    [key: string]: string;
  };
  name?: string;
  type: string;
  [s: string]: any;
}
interface ContextProp {
  context: Context;
  currencyList: string[]; // Ensure that the type of the prop is defined
}

const parseSummaryPayload = (message: any) => {
  if (message == null) return null;

  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message;
};

const CoHedgerTile: React.FunctionComponent<ContextProp> = ({
  context,
  currencyList,
}) => {
  const theme = useTheme();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [currencyPair, setCurrencyPair] = useState("");
  const [useContext, setUseContext] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string>("");
  const summaryCacheRef = useRef("");
  const summaryVersionRef = useRef(0);
  const [expirationDate, setExpirationDate] = useState<any>("None");

  const [tenorList, setTenorList] = useState([...FORWARD_ALLOWED_TENORS]);
  const [tenors, setTenors]: any = useState([...FORWARD_ALLOWED_TENORS]);

  const [asset_type, setAsset_type] = useState("");
  const [execution_type, setExecution_type] = useState("");

  const handleHedgeSubmit = (params: {
    currencyPair: string;
    assetType: string;
    executionType: string;
    expirationDate: string;
  }) => {
    setCurrencyPair(params.currencyPair);
    setAsset_type(params.assetType);
    setExecution_type(params.executionType);
    setExpirationDate(params.expirationDate);
    setUseContext(false);
    setHasSubmitted(true);
  };
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column", // Stack the components vertically
      height: "100%",
      width: "100%",
      padding: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500], // Background color for the combo tile
    },
    chartContainer: {
      height: "200px",
      "@media (max-height: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    chartContainer1: {
      marginTop: "10px",
      marginBottom: "20px",
      "@media (max-height: 480px)": {
        display: "none", // Hide chart on small screens
      },
    },
    insightsContainer: {
      flex: 1, // Take up remaining space
      overflow: "auto",
    },
    title: {
      color: "#E0E0E0",
      fontSize: "16px",
      fontWeight: "bolder",
      display: "flex",
      justifyContent: "space-between",
      padding: "4px",
      alignItems: "center",

      borderBottom: "1px solid rgba(255, 255, 255, 0.1)", // Align items vertically in the center
    },
    popoverContainer: {
      display: "flex",
      justifyContent: "end",
      width: "100%",
    },
  });

  const classes = useStyles();

  useEffect(() => {
    // Function to fetch and update chart data
    if (useContext) {
      try {
        if (context) {
          const currencyPairReceived = context.instruments[0].id.ticker;
          if (currencyList.includes(currencyPairReceived)) {
            setCurrencyPair(currencyPairReceived);
          }
          console.log("context Changed , Currency Pair Changed", currencyPair);
        } else {
          console.log("No Context Available");
        }
      } catch {
        console.log("Some Error Happened");
      }
    }
  }, [context]);

  useEffect(() => {
    setSessionVariable(currencyPair);
  }, [currencyPair]);

  useEffect(() => {
    if (!hasSubmitted || !currencyPair || !asset_type || !execution_type) return;

    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;

    const cleanup = handleFetchSummary({
      setSummary,
      summaryCacheRef,
      summaryVersionRef,
      currencyPair,
      asset_type,
      execution_type,
      setLoading,
      expirationDate,
    });

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [hasSubmitted, asset_type, execution_type, currencyPair, expirationDate]);

  if (!hasSubmitted) {
    return <CoHedgerLandingPage onSubmit={handleHedgeSubmit} />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <CoHedgerHeaderHeader
          currencyPair={currencyPair}
          assetType={asset_type}
          executionType={execution_type}
          expirationDate={expirationDate}
        />

        <div className="d-flex flex-row ">
          <div>
            <CoHedgerPopOverMenu
              setCurrencyPair={setCurrencyPair}
              setAsset_type={setAsset_type}
              setExecutionType={setExecution_type}
              setExpirationDate={setExpirationDate}
            />
          </div>
        </div>
      </div>
      <div className="my-2">{"Forward Points:"}</div>
      <div className={classes.chartContainer1}>
        <CoTraderForwardChart
          currencyPair={currencyPair}
          setTenorList={setTenorList}
          tenors={tenors}
          refreshIntervalMs={null}
        />{" "}
      </div>
      <div className={classes.insightsContainer}>
        <CoHedgerSummaryTileComponent
          summary={summary}
          loading={loading}
          currencyPair={currencyPair}
          assetType={asset_type}
          executionType={execution_type}
          expirationDate={expirationDate}
        />
      </div>
    </div>
  );
};

export default CoHedgerTile;

const handleFetchSummary = ({
  setSummary,
  summaryCacheRef,
  summaryVersionRef,
  currencyPair,
  asset_type,
  execution_type,
  setLoading,
  expirationDate,
}: any) => {
  setLoading(true);
  setSummary("");


  const user_id:any = localStorage.getItem("userId");
  const random_number = Math.floor(100000 + Math.random() * 900000);
  const chat_id = `HEDGE_ASSISTGPT-${user_id}-${random_number}`;
  const requestBody = {
    currency_pair: currencyPair ?? null,
    yahoo_finance_tickers: CurrencyList[currencyPair],
    asset_type: asset_type ?? null,
    execution_type: execution_type ?? null,
    chat_id,
    embedding_model: "text-embedding-ada-002",
    origin: "HEDGE_ASSIST",
    expiration_timestamp: expirationDate,
    summary: summaryCacheRef.current,
    summary_version: summaryVersionRef.current,
  };

  let isCancelled = false;
  let ws: WebSocket | null = null;

  const openSocket = async () => {
    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(chat_id, user_id);
      if (isCancelled) return;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        ws?.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        const t = data?.type;
        const msg = data?.message;

        if (t === "FINISH" || t === "DONE" || t === "END" || t === "COMPLETE") {
          setLoading(false);
          try {
            ws?.close();
          } catch {}
          return;
        }
        if (t === "SUMMARY_UPDATE") {
          const summaryPayload = parseSummaryPayload(msg);
          summaryCacheRef.current = summaryPayload?.summary || "";
          summaryVersionRef.current = summaryPayload?.version || 0;
          return;
        }
        if (t === "INFO" || t === "ERROR" || t === "MESSAGE_ID") {
          return;
        }

        if (msg != null) {
          setSummary((prev: string) => prev + String(msg));
        }
      };

      ws.onerror = (e) => {
        console.error("WS error", e);
        setLoading(false);
      };
    } catch (error) {
      if (!isCancelled) {
        console.error("WS auth failed", error);
        setLoading(false);
      }
    }
  };

  void openSocket();

  // cleanup only closes if still active
  return () => {
    isCancelled = true;
    try {
      ws?.close();
    } catch {}
  };
};
